import { type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';

interface SystemStatus {
  env: string;
  envError: string | null;
  google: { authenticated: boolean; email: string | null };
  jira: { configured: boolean; host?: string; email?: string };
  xray: { configured: boolean };
  openai: { configured: boolean; model?: string };
}

interface CredsStatus {
  jira: { configured: boolean; email: string | null; tokenLooksInvalid?: boolean };
  xray: { configured: boolean };
  gitlab?: { configured: boolean };
}

function CardCopy({ children }: { children: ReactNode }) {
  return (
    <div className="muted small flex-col" style={{ gap: 8, maxWidth: 760, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

export function Settings() {
  const { user } = useAuth();
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creds, setCreds] = useState<CredsStatus | null>(null);
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraToken, setJiraToken] = useState('');
  const [xrayId, setXrayId] = useState('');
  const [xraySecret, setXraySecret] = useState('');
  const [gitlabToken, setGitlabToken] = useState('');
  const [credsMsg, setCredsMsg] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setStatus(await api.get<SystemStatus>('/api/system/status'));
      const c = await api.get<CredsStatus>('/api/credentials');
      setCreds(c);
      // Prefill non-secret fields so "Save" works without retyping email/id.
      if (c.jira.email) setJiraEmail(c.jira.email);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const connectGoogle = async () => {
    const data = await api.get<{ url: string }>('/api/auth/google/url');
    const popup = window.open(data.url, 'google-oauth', 'width=520,height=640');
    if (!popup) {
      window.location.href = data.url;
      return;
    }
    const interval = setInterval(async () => {
      if (popup.closed) {
        clearInterval(interval);
        await refresh();
      }
    }, 1500);
  };

  const logoutGoogle = async () => {
    await api.post('/api/auth/google/logout');
    await refresh();
  };

  const saveCreds = async () => {
    setCredsMsg(null);
    try {
      const body: {
        jira?: { email?: string; token?: string };
        xray?: { clientId?: string; clientSecret?: string };
        gitlab?: { token?: string };
      } = {};

      const jiraTokenTrim = jiraToken.trim();
      const jiraEmailTrim = jiraEmail.trim();
      if (jiraEmailTrim || jiraTokenTrim) {
        body.jira = {};
        if (jiraEmailTrim) body.jira.email = jiraEmailTrim;
        // Ignore browser-autofill bullets; only send a real new token.
        if (jiraTokenTrim && !/^•+$/.test(jiraTokenTrim) && jiraTokenTrim !== '********') {
          if (jiraTokenTrim.length < 40) {
            setCredsMsg(
              'Jira API token looks too short. Create one at id.atlassian.com → Security → API tokens and paste the full value.',
            );
            return;
          }
          body.jira.token = jiraTokenTrim;
        }
      }

      const xrayIdTrim = xrayId.trim();
      const xraySecretTrim = xraySecret.trim();
      if (xrayIdTrim || xraySecretTrim) {
        body.xray = {};
        if (xrayIdTrim && !/^•+$/.test(xrayIdTrim) && xrayIdTrim !== '********') {
          body.xray.clientId = xrayIdTrim;
        }
        if (xraySecretTrim && !/^•+$/.test(xraySecretTrim) && xraySecretTrim !== '********') {
          body.xray.clientSecret = xraySecretTrim;
        }
      }

      const gitlabTokenTrim = gitlabToken.trim();
      if (gitlabTokenTrim && !/^•+$/.test(gitlabTokenTrim) && gitlabTokenTrim !== '********') {
        if (gitlabTokenTrim.length < 20) {
          setCredsMsg(
            'GitLab token looks too short. Create a Personal Access Token in GitLab and paste the full value.',
          );
          return;
        }
        body.gitlab = { token: gitlabTokenTrim };
      }

      if (!body.jira && !body.xray && !body.gitlab) {
        setCredsMsg('Enter at least one new value to save (Jira, Xray, or GitLab).');
        return;
      }

      setCreds(await api.put<CredsStatus>('/api/credentials', body));
      setJiraToken('');
      setXraySecret('');
      setXrayId('');
      setGitlabToken('');
      setCredsMsg('Saved.');
      await refresh();
    } catch (e: unknown) {
      setCredsMsg((e as Error).message);
    }
  };

  return (
    <div className="flex-col">
      <h1 style={{ margin: 0 }}>Settings</h1>
      <p className="muted small" style={{ marginTop: 0, maxWidth: 760 }}>
        Credentials and shared knowledge for Sidekick. Fill your own API tokens
        first, connect Drive, then use the catalogues below (ontology, test
        guidance, studios) when a studio or TLIB needs extra teaching.
      </p>

      {error && (
        <div className="card" style={{ borderColor: 'var(--bad)' }}>
          {error}
        </div>
      )}

      <div className="card">
        <h2>My API credentials</h2>
        <CardCopy>
          <p style={{ margin: 0 }}>
            <strong>What.</strong> Your personal Jira, Xray, and GitLab tokens,
            stored encrypted on the server. Shared OpenAI / Vertex keys stay in
            the VM <code>.env</code> — they are not entered here. Background
            jobs (pull source, analyse tests, create bugs) use the credentials
            of whoever queued them.
          </p>
          <p style={{ margin: 0 }}>
            <strong>When.</strong> First login, or whenever a token expires /
            Jira says you are unauthenticated.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Jira email = the Atlassian account that can see SR / TEXEC issues.</li>
            <li>
              Jira API token = from{' '}
              <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer">
                id.atlassian.com → Security → API tokens
              </a>
              , not your password.
            </li>
            <li>Xray client id + secret = from Jira Xray → API Keys (the pair used to load TLIB tests).</li>
            <li>GitLab PAT = a token that can clone the studio&apos;s <code>client-critical-files</code> project.</li>
            <li>Leave a secret field blank to keep the saved value. Use Clear mine to drop your personal tokens and fall back to the server env keys.</li>
          </ul>
          <p style={{ margin: 0 }}>
            <strong>Example.</strong> Paste a new Atlassian API token, click Save,
            then open a TEXEC — Phase 1 should load the Game task as you, not as
            the shared lab account.
          </p>
        </CardCopy>
        {creds && (
          <p className="muted small">
            Status — Jira:{' '}
            {creds.jira.configured
              ? `configured as ${creds.jira.email}`
              : creds.jira.tokenLooksInvalid
                ? `saved email ${creds.jira.email} but token looks invalid — re-paste a full Atlassian API token or Clear mine`
                : 'using shared .env fallback'}{' '}
            · Xray: {creds.xray.configured ? 'configured' : 'using shared .env fallback'} · GitLab:{' '}
            {creds.gitlab?.configured ? 'configured' : 'using shared .env fallback'}
          </p>
        )}
        <div className="flex-col" style={{ gap: 8, maxWidth: 480 }}>
          <label>
            Jira email
            <input
              value={jiraEmail}
              onChange={(e) => setJiraEmail(e.target.value)}
              placeholder="you@riskcherry.com"
              autoComplete="username"
            />
          </label>
          <label>
            Jira API token
            <input
              type="password"
              value={jiraToken}
              onChange={(e) => setJiraToken(e.target.value)}
              placeholder={
                creds?.jira.configured ? '•••••••• (saved — leave blank to keep)' : '••••••••'
              }
              autoComplete="new-password"
            />
          </label>
          <label>
            Xray client id
            <input
              value={xrayId}
              onChange={(e) => setXrayId(e.target.value)}
              placeholder={
                creds?.xray.configured ? '•••••••• (saved — leave blank to keep)' : ''
              }
              autoComplete="off"
            />
          </label>
          <label>
            Xray client secret
            <input
              type="password"
              value={xraySecret}
              onChange={(e) => setXraySecret(e.target.value)}
              placeholder={
                creds?.xray.configured ? '•••••••• (saved — leave blank to keep)' : '••••••••'
              }
              autoComplete="new-password"
            />
          </label>
          <label>
            GitLab personal access token
            <input
              type="password"
              value={gitlabToken}
              onChange={(e) => setGitlabToken(e.target.value)}
              placeholder={
                creds?.gitlab?.configured ? '•••••••• (saved — leave blank to keep)' : 'glpat-…'
              }
              autoComplete="new-password"
            />
          </label>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-primary" type="button" onClick={() => void saveCreds()}>
              Save credentials
            </button>
            <button
              className="btn"
              type="button"
              onClick={() =>
                void api.delete('/api/credentials').then((c) => {
                  setCreds(c as CredsStatus);
                  setJiraEmail('');
                  setJiraToken('');
                  setXrayId('');
                  setXraySecret('');
                  setGitlabToken('');
                  setCredsMsg('Cleared personal credentials.');
                })
              }
            >
              Clear mine
            </button>
          </div>
          {credsMsg && <p className="muted small">{credsMsg}</p>}
        </div>
      </div>

      {status && (
        <>
          <div className="card">
            <h2>Environment</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> Read-only check that the server process has
                the required <code>.env</code> keys (OpenAI, OAuth client, session
                secret, and so on). You cannot edit them here.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> If Sidekick will not start, or AI / login
                suddenly fail for everyone (not just you).
              </p>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> Green “all required env vars present”
                means the VM is wired. If this card is red, an admin must fix the
                file on the server — personal tokens above will not help.
              </p>
            </CardCopy>
            <p
              className={status.env === 'ok' ? 'muted' : ''}
              style={{ color: status.env === 'ok' ? undefined : 'var(--bad)' }}
            >
              {status.env === 'ok' ? 'All required env vars present.' : status.envError}
            </p>
          </div>

          <div className="card">
            <h2>Google Drive</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> OAuth for <em>your</em> Google account so
                Sidekick can read TEXEC Drive folders. Separate from Google login
                to this app.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> Before Phase 4 (game-help / paytable
                screenshots) or when importing a gameplay video from a Drive link
                in Phase 5.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Click Connect Google Drive and finish the popup as the account that can open the TEXEC folder.</li>
                <li>Disconnect only if you need to switch accounts or revoke access.</li>
              </ul>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> After connect, Phase 4 can download
                Game Help screenshots from the Drive link on the TEXEC; Phase 5
                can take a <code>drive.google.com/file/d/…</code> URL and import
                the video without uploading from disk.
              </p>
            </CardCopy>
            <div className="row">
              {status.google.authenticated ? (
                <>
                  <span className="badge good">
                    Connected{status.google.email ? `: ${status.google.email}` : ''}
                  </span>
                  <span className="spacer" />
                  <button className="btn" onClick={() => void logoutGoogle()}>
                    Disconnect
                  </button>
                </>
              ) : (
                <>
                  <span className="badge bad">Not connected</span>
                  <span className="spacer" />
                  <button className="btn btn-primary" onClick={() => void connectGoogle()}>
                    Connect Google Drive
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="card">
            <h2>Jira (shared fallback)</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> Status of the <em>server</em> Jira keys in{' '}
                <code>.env</code> (<code>JIRA_HOST</code> / email / token). This
                is not an editor. If your personal token above is missing,
                Sidekick uses this lab account instead.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> Ignore this in daily work. Fill My API
                credentials so comments, transitions, and bugs are attributed to
                you.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> “Configured as lab@…” means the VM can
                still talk to Jira if a tester has not saved a personal token
                yet. That is a fallback, not the preferred path.
              </p>
            </CardCopy>
            {status.jira.configured ? (
              <p className="muted small">
                Env fallback: <span className="mono">{status.jira.host}</span> as{' '}
                <span className="mono">{status.jira.email}</span>.
              </p>
            ) : (
              <p style={{ color: 'var(--bad)' }}>Not configured. Fill JIRA_HOST/EMAIL/TOKEN in .env.</p>
            )}
          </div>

          <div className="card">
            <h2>Xray (shared fallback)</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> Status of the server Xray API key pair
                used to load TLIB tests when you have not saved your own Xray
                id/secret above.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> Same as Jira: prefer personal Xray keys in
                My API credentials. This card only tells you the VM fallback exists.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> Phase 1 “Load tests” still works for
                someone with no personal Xray keys if this card says configured
                — the request is made as the shared app.
              </p>
            </CardCopy>
            <p className="muted small">
              {status.xray.configured
                ? 'Env fallback configured.'
                : 'Not configured. Set XRAY_CLIENT_ID/SECRET in .env.'}
            </p>
          </div>

          <div className="card">
            <h2>OpenAI</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> Status of the shared OpenAI key on the
                server (Phase 7, video frames, chat, Look at that adapters that
                use OpenAI). Vertex / Gemini keys are also env-only. There is
                nothing to paste here.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> If every AI action fails for all users,
                check this card and the Environment card. Individual testers
                cannot fix it from Settings.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> “Configured (model: gpt-5.4)” means
                Phase 7 can call OpenAI. A missing key here is an admin{' '}
                <code>.env</code> job, not a personal-token job.
              </p>
            </CardCopy>
            <p className="muted small">
              {status.openai.configured
                ? `Configured (model: ${status.openai.model})`
                : 'Not configured. Set OPENAI_API_KEY in .env.'}
            </p>
          </div>

          <div className="card">
            <h2>Studios</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> One recipe per client: how to build a play
                URL (Phase 2), which GitLab project Phase 3 clones, optional
                Confluence wiki sync, and optional AI notes for chrome (hamburger
                vs Autoplay, debug/cheat menus).
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> A new studio, a changed lobby URL, or
                when Look at that / Phase 7 keep rediscovering the same HUD
                tricks on every game from that client.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Open the studio → set play URL mode and template → save.</li>
                <li>Map the GitLab project so Pull Source Code clones the right repo.</li>
                <li>Optional: sync Confluence for the auto wiki brief; write AI knowledge for operator notes (not the wiki brief).</li>
              </ul>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> Leander: template{' '}
                <code>{'{{playUrlBase}}?game={{gameId}}&currency={{currency}}'}</code>
                , GitLab project id filled, AI knowledge “History is in the
                hamburger, not the circular-arrows Autoplay control.” Phase 2
                Generate Environment Links then emits working URLs for each market.
              </p>
            </CardCopy>
            <div className="row">
              <Link to="/settings/studios" className="btn small btn-primary">
                Manage studios
              </Link>
            </div>
          </div>

          {user?.role === 'admin' && (
            <div className="card">
              <h2>Users</h2>
              <CardCopy>
                <p style={{ margin: 0 }}>
                  <strong>What.</strong> Allowlist for Google login. An account
                  must be <code>@riskcherry.com</code> <em>and</em> listed here
                  (superadmins in env always get in). There is no extra password.
                </p>
                <p style={{ margin: 0 }}>
                  <strong>When.</strong> A colleague sees “Your account is not
                  authorized. Ask an administrator to add you.”
                </p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Add their Google email, optional display name, role tester or admin.</li>
                  <li>Deactivate to block login without deleting the row. Superadmins cannot be deleted.</li>
                </ul>
                <p style={{ margin: 0 }}>
                  <strong>Example.</strong> Add <code>colleague@riskcherry.com</code>{' '}
                  as tester → they sign in with that Google account on the login
                  page.
                </p>
              </CardCopy>
              <div className="row">
                <Link to="/settings/users" className="btn small btn-primary">
                  Manage users
                </Link>
              </div>
            </div>
          )}

          <div className="card">
            <h2>Ontology</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> The live concept catalogue the AI uses
                (History, Autoplay, buy feature, clock, …). Each concept has a
                definition, an invariant (“what it <em>does</em>”), aliases, and
                look-alikes it must not be confused with. Only the cards a test
                needs are injected — this replaced the old flat glossary.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> The AI names a control wrong, or a studio
                brands a standard mechanic (“Buy Chance”, “Ante”, “Savannah
                Strike Extra”).
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Find the concept (e.g. <code>history</code>) and add an alias or disambiguation.</li>
                <li>Do not create a leftover glossary term — that path is gone and was never reaching the prompts.</li>
              </ul>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> Live play keeps clicking right-column
                circular-arrows as History. Edit the <code>history</code> concept:
                alias “clock / wrapped arrows / the word History”; disambiguate
                vs Autoplay circular-arrows. Next Look at that / Phase 7 run
                gets that card.
              </p>
            </CardCopy>
            <div className="row">
              <Link to="/settings/ontology" className="btn small btn-primary">
                Manage ontology
              </Link>
            </div>
          </div>

          <div className="card">
            <h2>Corrections &amp; Eval</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> A logbook of tester overrides, grouped by
                why the AI was wrong, plus a scoreboard that checks whether
                aliases actually resolve. Nothing here trains a model — it tells
                you which layer to fix.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> After you change an AI verdict on a test
                and pick a failure category (recognition / observation / judgment
                / data). Review the pile when the same miss repeats.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li><strong>Recognition</strong> → add an alias on the ontology concept.</li>
                <li><strong>Observation</strong> → fix capture (video/screenshots), not the catalogue.</li>
                <li><strong>Judgment</strong> → tighten the concept invariant or disambiguation.</li>
                <li><strong>Data</strong> → fix Test guidance or the Jira/Xray text.</li>
              </ul>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> Three “recognition” misses on “Buy
                Chance” → add that alias on <code>ante_bet</code> (or the right
                concept) → Eval dashboard → Run recognition eval and watch the
                alias score move.
              </p>
            </CardCopy>
            <div className="row" style={{ gap: 8 }}>
              <Link to="/settings/corrections" className="btn small btn-primary">
                Review corrections
              </Link>
              <Link to="/settings/eval" className="btn small">
                Eval dashboard
              </Link>
            </div>
          </div>

          <div className="card">
            <h2>Test guidance</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> Per-TLIB cheatsheet on top of Xray
                (summary, full description, steps). Look at that and Phase 7
                both read it. Notes = how to play; PASSED / FAILED / N/A = when
                to stop or skip.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> A test is easy to mis-play in the live
                game, or the AI keeps exploring after the expected result is
                already visible.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Add or open the TLIB key (e.g. TLIB-95).</li>
                <li>Notes = ordered play steps and gotchas.</li>
                <li>PASSED / FAILED = the on-screen evidence that ends the test. Live play stops when PASSED is visible.</li>
              </ul>
              <p style={{ margin: 0 }}>
                <strong>Example (TLIB-95 History).</strong> Notes: “Find History
                in the hamburger / HUD — do not confuse Autoplay circular-arrows
                with History. Place one bet. Open History and confirm that round
                is listed.” PASSED: “The round appears in the log with
                reconstruction / date / balances as required.”
              </p>
            </CardCopy>
            <div className="row">
              <Link to="/settings/test-guidance" className="btn small btn-primary">
                Manage guidance
              </Link>
            </div>
          </div>

          <div className="card">
            <h2>Look at that calibration</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> The global walk Look at that follows when
                you press <strong>Calibrate</strong> on a run. It maps chrome
                (menus, HUD, look-alike icons) into the run atlas and game
                memory. It is not a pass/fail test and is not per-TLIB.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> A studio&apos;s UI fools the default walk
                (Autoplay vs History, hidden hamburger, debug menu). Per-test
                scripts still belong in Test guidance; per-studio chrome that
                applies to every game can also go on the studio AI knowledge
                field.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> “Open the hamburger, screenshot
                History, do not click the right-column circular arrows (that is
                Autoplay). Note where Help / Paytable live, then return to the
                base game.”
              </p>
            </CardCopy>
            <div className="row">
              <Link to="/settings/calibration" className="btn small btn-primary">
                Edit calibration brief
              </Link>
            </div>
          </div>

          <div className="card">
            <h2>Evidence labels</h2>
            <CardCopy>
              <p style={{ margin: 0 }}>
                <strong>What.</strong> The Jira “Indicator label” vocabulary.
                Keys must match what Xray puts on a TLIB. Tick <em>Visual</em>
                for labels the screenshot / video-frame tagger may apply
                (<code>game-rules</code>, <code>ui-controls</code>, …). Non-visual
                labels (network, measurement, client-action) stay for completeness
                but never appear on a frame chip.
              </p>
              <p style={{ margin: 0 }}>
                <strong>When.</strong> Jira adds or renames an indicator, or
                Phase 7 is drowning a test in the wrong kind of footage.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Keep the key identical to the Jira label.</li>
                <li>Tick Visual only if a human could see that evidence on a screenshot or video frame.</li>
              </ul>
              <p style={{ margin: 0 }}>
                <strong>Example.</strong> A help-page TLIB labelled{' '}
                <code>game-rules</code> only pulls frames tagged that way, so
                the aggregator is not flooded with spin footage. If the tagger
                never assigns <code>game-rules</code>, edit that label&apos;s
                definition and re-analyse the video.
              </p>
            </CardCopy>
            <div className="row">
              <Link to="/settings/evidence-labels" className="btn small btn-primary">
                Manage labels
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
